// src/components/CustomPaginator.tsx
import React from 'react';

interface CustomPaginatorProps {
  page: number;
  totalPages: number;
  totalRecords: number;
  pageSize: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange?: (newSize: number) => void; // ← optional
  pageSizeOptions?: number[];                   // ← optional, defaults below
  showInfoBar?: boolean;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const CustomPaginator: React.FC<CustomPaginatorProps> = ({
  page,
  totalPages,
  totalRecords,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  showInfoBar = true,
}) => {
  if (totalPages <= 1 && !onPageSizeChange) return null;

  const startRecord = (page - 1) * pageSize + 1;
  const endRecord = Math.min(page * pageSize, totalRecords);

  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, page + 2);

    if (page <= 3) end = Math.min(totalPages, 5);
    if (page >= totalPages - 2) start = Math.max(1, totalPages - 4);

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }

    for (let i = start; i <= end; i++) pages.push(i);

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  const atFirst = page === 1;
  const atLast = page === totalPages;

  return (
    <>
      <style>{`
        .cp-nav-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          border-radius: 9px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          cursor: pointer;
          transition: background .15s ease, border-color .15s ease, color .15s ease, transform .1s ease;
          padding: 0;
        }
        .cp-nav-btn i { font-size: 12px; }
        .cp-nav-btn:hover:not(:disabled) {
          background: #dbeafe;
          border-color: #bfdbfe;
          color: #1e3a8a;
        }
        .cp-nav-btn:active:not(:disabled) { transform: scale(0.94); }
        .cp-nav-btn:disabled {
          color: #cbd5e1;
          background: #f8fafc;
          border-color: #eef1f5;
          cursor: not-allowed;
        }

        .cp-page-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 34px;
          height: 34px;
          padding: 0 6px;
          border-radius: 9px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: background .15s ease, color .15s ease, border-color .15s ease, transform .1s ease, box-shadow .15s ease;
          background: white;
          color: #475569;
          border: 1px solid #e2e8f0;
        }
        .cp-page-btn:hover:not(.active) {
          background: #dbeafe;
          border-color: #bfdbfe;
          color: #1e3a8a;
        }
        .cp-page-btn:active { transform: scale(0.94); }
        .cp-page-btn.active {
          background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
          color: white;
          border-color: transparent;
          box-shadow: 0 3px 10px rgba(59,130,246,.4);
          cursor: default;
        }

        .cp-ellipsis {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 34px;
          color: #94a3b8;
          font-weight: 600;
          font-size: 13px;
        }

        .cp-size-select {
          padding: 6px 28px 6px 10px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          font-size: 13px;
          color: #1f2937;
          font-weight: 600;
          background: white;
          cursor: pointer;
          outline: none;
          appearance: none;
          -webkit-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2364748b' stroke-width='1.5' fill='none' fill-rule='evenodd' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 10px center;
          transition: border-color .15s ease;
        }
        .cp-size-select:hover { border-color: #bfdbfe; }
        .cp-size-select:focus { border-color: #3b82f6; }

        .cp-goto-input {
          width: 52px;
          padding: 7px 8px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          font-size: 13px;
          font-weight: 600;
          color: #1f2937;
          text-align: center;
          outline: none;
          transition: border-color .15s ease;
        }
        .cp-goto-input:focus { border-color: #3b82f6; }
        .cp-goto-input::-webkit-outer-spin-button,
        .cp-goto-input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .cp-goto-input[type=number] { -moz-appearance: textfield; }
      `}</style>

      {showInfoBar && (
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid #f3f4f6',
          background: '#fafbfc',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '8px',
              background: 'linear-gradient(135deg, rgba(30,58,138,0.1) 0%, rgba(59,130,246,0.08) 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <i className="pi pi-list" style={{ fontSize: '14px', color: '#1e40af' }} />
            </div>
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
              Showing {startRecord} - {endRecord} of {totalRecords} entries
            </span>
          </div>
          <div style={{
            padding: '8px 16px', borderRadius: '12px',
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            boxShadow: '0 4px 12px rgba(59,130,246,.35)'
          }}>
            <span style={{ fontSize: '13px', color: 'white', fontWeight: '700' }}>
              Page {page} of {totalPages}
            </span>
          </div>
        </div>
      )}

      <div style={{
        background: '#fafbfc',
        borderTop: showInfoBar ? 'none' : '1px solid #e5e7eb',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        {/* Left: per-page selector (if enabled) or static label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {onPageSizeChange ? (
            <>
              <span style={{ fontSize: '13px', color: '#64748b' }}>Rows per page</span>
              <select
                className="cp-size-select"
                value={pageSize}
                onChange={(e) => {
                  onPageSizeChange(Number(e.target.value));
                  onPageChange(1); // reset to page 1 on size change
                }}
              >
                {pageSizeOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </>
          ) : (
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
              {pageSize} per page
            </span>
          )}
        </div>

        {/* Center: page buttons */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              className="cp-nav-btn"
              onClick={() => onPageChange(1)}
              disabled={atFirst}
              aria-label="First page"
              title="First page"
            >
              <i className="pi pi-angle-double-left" />
            </button>
            <button
              className="cp-nav-btn"
              onClick={() => onPageChange(page - 1)}
              disabled={atFirst}
              aria-label="Previous page"
              title="Previous page"
            >
              <i className="pi pi-angle-left" />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', margin: '0 4px' }}>
              {generatePageNumbers().map((p, idx) =>
                typeof p === 'string' ? (
                  <span key={`ellipsis-${idx}`} className="cp-ellipsis">···</span>
                ) : (
                  <button
                    key={p}
                    className={`cp-page-btn ${p === page ? 'active' : ''}`}
                    onClick={() => p !== page && onPageChange(p as number)}
                    aria-label={`Page ${p}`}
                    aria-current={p === page ? 'page' : undefined}
                  >
                    {p}
                  </button>
                )
              )}
            </div>

            <button
              className="cp-nav-btn"
              onClick={() => onPageChange(page + 1)}
              disabled={atLast}
              aria-label="Next page"
              title="Next page"
            >
              <i className="pi pi-angle-right" />
            </button>
            <button
              className="cp-nav-btn"
              onClick={() => onPageChange(totalPages)}
              disabled={atLast}
              aria-label="Last page"
              title="Last page"
            >
              <i className="pi pi-angle-double-right" />
            </button>
          </div>
        )}

        {/* Right: go-to input */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: '#64748b' }}>Go to</span>
            <input
              className="cp-goto-input"
              type="number"
              min="1"
              max={totalPages}
              value={page}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (val >= 1 && val <= totalPages) onPageChange(val);
              }}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default CustomPaginator;